/*
  Origin hotspots
  Ported from Horizon's product-hotspot component, without its framework or quick-add modal.

  Each <origin-hotspot> holds a trigger button and a non-modal <dialog> popover:
    - devices with a mouse open the popover on hover (after a short delay) and close it when the pointer leaves
    - clicking or tapping the trigger toggles it, so touch and keyboard users get the same popover
    - the popover is placed to the right, left or centred above/below the hotspot, whichever keeps it
      inside the image (the closest [data-origin-hotspots] element)
    - clicking outside, pressing Escape (even after a hover) or tabbing away closes it; one popover at a time
    - selecting a hotspot block in the theme editor opens its popover

  Markup:
    <origin-hotspot>
      <button data-origin-hotspot-trigger aria-expanded="false" aria-controls="…">…</button>
      <dialog id="…">…</dialog>
    </origin-hotspot>
*/
if (!customElements.get('origin-hotspot')) {
  const EDGE_GAP = 10; // Minimum space between the popover and the image edge
  const HOVER_DELAY = 120;

  class OriginHotspot extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-origin-hotspot-trigger]');
      this.dialog = this.querySelector('dialog');
      if (!this.trigger || !this.dialog) return;

      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

      // With a mouse, hover has usually opened it already, so a click only ever opens.
      // Touch and keyboard clicks toggle.
      this.onTriggerClick = (event) => {
        event.preventDefault();
        clearTimeout(this.timer);
        if (this.dialog.open && !this.hoverQuery.matches) this.close();
        else this.open();
      };

      this.onPointerEnter = () => {
        if (!this.hoverQuery.matches || this.dialog.open) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.open(), HOVER_DELAY);
      };

      // The popover is a child of this element and sits against the trigger's hit area,
      // so moving from the trigger onto the popover doesn't count as leaving
      this.onPointerLeave = () => {
        if (!this.hoverQuery.matches) return;
        clearTimeout(this.timer);
        this.close();
      };

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };

      // Listens on the document while open, so Escape works after a hover too (focus isn't inside then)
      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.dialog.open) return;
        const hadFocus = this.contains(document.activeElement);
        this.close();
        if (hadFocus) this.trigger.focus();
      };

      this.onFocusOut = (event) => {
        if (this.dialog.open && !(event.relatedTarget instanceof Node && this.contains(event.relatedTarget))) {
          this.close();
        }
      };

      this.onBlockSelect = (event) => {
        if (event.target === this) this.open();
      };

      this.onBlockDeselect = (event) => {
        if (event.target === this) this.close();
      };

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.trigger.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusout', this.onFocusOut);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    get container() {
      return this.closest('[data-origin-hotspots]') || this.parentElement;
    }

    open() {
      if (this.dialog.open) return;

      this.container
        ?.querySelectorAll('origin-hotspot')
        .forEach((hotspot) => hotspot !== this && hotspot.close?.());

      // The open attribute rather than show(), so hovering doesn't move focus into the popover
      this.dialog.style.visibility = 'hidden';
      this.dialog.setAttribute('open', '');
      this.place();
      this.dialog.style.removeProperty('visibility');

      this.toggleAttribute('data-open', true);
      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.onKeydown);
      // Defer so the click that opened the popover doesn't immediately close it,
      // and skip it if the popover has already closed again by then
      setTimeout(() => {
        if (this.dialog.open) document.addEventListener('click', this.onDocumentClick);
      });
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.removeAttribute('open');
      this.removeAttribute('data-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* Choose where the popover sits so it stays inside the image (same rules as Horizon) */
    place() {
      const { dialog } = this;
      dialog.removeAttribute('data-placement');
      dialog.style.removeProperty('--origin-dialog-offset-y');
      dialog.style.removeProperty('--origin-dialog-offset-x');

      const container = this.container.getBoundingClientRect();
      const hotspot = this.getBoundingClientRect();
      const { width, height } = dialog.getBoundingClientRect();

      const left = hotspot.left - container.left;
      const right = hotspot.right - container.left;
      const top = hotspot.top - container.top;
      const bottom = hotspot.bottom - container.top;

      let x = 'center';
      if (container.width - right - EDGE_GAP >= width) x = 'right';
      else if (left - EDGE_GAP >= width) x = 'left';

      let y = 'bottom';
      let offsetY = 0;
      let offsetX = 0;

      if (x !== 'center') {
        // Beside the hotspot: top-aligned, flipping to bottom-aligned if it would overflow,
        // then nudged to stay inside the image
        let start = top;
        if (top + height > container.height - EDGE_GAP) {
          y = 'top';
          start = bottom - height;
        }
        if (start < EDGE_GAP) offsetY = EDGE_GAP - start;
        else if (start + height > container.height - EDGE_GAP) offsetY = container.height - EDGE_GAP - (start + height);
      } else {
        // Above or below the hotspot, centred on it but kept inside the image horizontally
        if (container.height - bottom >= height + EDGE_GAP) y = 'bottom';
        else if (top >= height + EDGE_GAP) y = 'top';
        else y = top < container.height / 2 ? 'bottom' : 'top';

        const centredLeft = left + hotspot.width / 2 - width / 2;
        const clampedLeft = Math.min(Math.max(centredLeft, EDGE_GAP), container.width - width - EDGE_GAP);
        offsetX = clampedLeft - centredLeft;
      }

      dialog.dataset.placement = `${x} ${y}`;
      if (offsetY) dialog.style.setProperty('--origin-dialog-offset-y', `${offsetY}px`);
      if (offsetX) dialog.style.setProperty('--origin-dialog-offset-x', `${offsetX}px`);
    }
  }

  customElements.define('origin-hotspot', OriginHotspot);
}
